import React, { Component, Suspense } from "react";
import { getAdapter } from "./WidgetRegistry";
import WidgetErrorBoundary from "./common/WidgetErrorBoundary";
import WidgetLoader from "./common/WidgetLoader";
import configData from "../config.json";

/**
 * WidgetRenderer - Fetches widget config, resolves adapter from registry, renders with parsed JSON configs.
 * Class component per project convention.
 *
 * Props:
 *   - widgetConfig: Pre-loaded widget config (from WidgetPageRenderer) including typeKey, adapterClass, etc.
 *   - widgetInstanceId: (alternative) Load config by ID via API
 *   - pageRoute: The current page route
 *   - onEvent: Callback for cross-widget events (from WidgetEventContext)
 */
export default class WidgetRenderer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      config: props.widgetConfig || null,
      loading: !props.widgetConfig,
      error: null,
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
    };
  }

  componentDidMount() {
    if (!this.state.config && this.props.widgetInstanceId) {
      this.fetchWidgetConfig();
    }
  }

  fetchWidgetConfig = () => {
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append("widget_instances_Id", this.props.widgetInstanceId);

    fetch(configData.api_url + "C_Widget_framework/get_widget_instance", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success" && data.detail) {
          this.setState({ config: data.detail, loading: false });
        } else {
          this.setState({ error: data.message || "Failed to load widget config", loading: false });
        }
      })
      .catch((err) => {
        this.setState({ error: "Network error loading widget", loading: false });
        console.error("WidgetRenderer fetch error:", err);
      });
  };

  /**
   * Parse JSON config fields safely
   */
  parseJsonConfig = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;
    try {
      return JSON.parse(value);
    } catch (e) {
      console.warn("Failed to parse JSON config:", value);
      return null;
    }
  };

  render() {
    const { loading, error, config } = this.state;

    if (loading) {
      return <WidgetLoader loading={true} />;
    }

    if (error) {
      return <WidgetLoader error={error} onRetry={this.fetchWidgetConfig} />;
    }

    if (!config || !config.typeKey) {
      console.warn("[WR] Missing typeKey. Config keys:", config ? Object.keys(config) : "null");
      return <WidgetLoader error="Widget configuration is missing or incomplete" />;
    }

    const AdapterComponent = getAdapter(config.typeKey);
    if (!AdapterComponent) {
      return <WidgetLoader error={`No adapter found for widget type: ${config.typeKey}`} />;
    }

    // Parse all JSON config fields
    const parsedConfig = {
      ...config,
      columnsConfig: this.parseJsonConfig(config.columnsConfig),
      actionsConfig: this.parseJsonConfig(config.actionsConfig),
      tableConfig: this.parseJsonConfig(config.tableConfig),
      formConfig: this.parseJsonConfig(config.formConfig),
      chartConfig: this.parseJsonConfig(config.chartConfig),
      filterConfig: this.parseJsonConfig(config.filterConfig),
      customProps: this.parseJsonConfig(config.customProps),
      typeDefaultConfig: this.parseJsonConfig(config.typeDefaultConfig),
    };

    return (
      <WidgetErrorBoundary widgetName={config.instanceName}>
        <Suspense fallback={<WidgetLoader loading={true} minHeight="100px" />}>
          <AdapterComponent
            config={parsedConfig}
            pageRoute={this.props.pageRoute}
            onEvent={this.props.onEvent}
            pageFilters={this.props.pageFilters}
            cellConfig={this.props.cellConfig}
          />
        </Suspense>
      </WidgetErrorBoundary>
    );
  }
}
